<template>
  <section v-if="visible" id="android-health-connect" class="android-health" aria-labelledby="android-health-title">
    <header class="android-health__header">
      <span class="android-health__icon"><v-icon color="primary">mdi-heart-pulse</v-icon></span>
      <div class="android-health__heading">
        <span class="android-health__eyebrow">DEIN HANDY. DEINE DATEN.</span>
        <h2 id="android-health-title">Gesundheitsdaten</h2>
        <p>{{ connectionLabel }}</p>
      </div>
      <v-btn v-if="health.enabled" icon dark aria-label="Gesundheitsdaten verwalten" @click="openSettings"><v-icon>mdi-tune</v-icon></v-btn>
    </header>

    <p v-if="health.status === 'app_update_required'" class="android-health__notice">Für Gesundheitsdaten brauchst du die neue Android-App mit Health Connect.</p>
    <p v-else-if="health.status === 'unavailable'" class="android-health__notice">Health Connect ist auf diesem Gerät nicht verfügbar. Dein Roody-Training kannst du weiterhin verwenden.</p>
    <div v-else-if="health.status === 'install_required'" class="android-health__intro">
      <p>Installiere oder aktualisiere Health Connect, um freigegebene Daten aus deiner Health-App abzurufen.</p>
      <v-btn rounded depressed color="primary" @click="service.settings()">Health Connect öffnen</v-btn>
    </div>
    <template v-else>
      <div v-if="!health.enabled" class="android-health__intro">
        <p>Deine Trainings und Tageswerte aus deiner Health-App, direkt hier. Du wählst aus, welche Daten du freigibst.</p>
        <v-btn rounded depressed color="primary" :loading="health.busy" :disabled="!health.supported" @click="openSettings">
          <v-icon left small>mdi-link-variant</v-icon> Gesundheitsdaten verbinden
        </v-btn>
      </div>
      <template v-else>
        <div class="android-health__sync" aria-live="polite">
          <span>{{ health.busy ? 'Daten werden aktualisiert …' : lastSyncLabel }}</span>
          <v-btn icon small dark :loading="health.busy" :disabled="health.busy" aria-label="Gesundheitsdaten aktualisieren" @click="service.refresh(true)"><v-icon>mdi-refresh</v-icon></v-btn>
        </div>
        <p v-if="health.missingCategories.length" class="android-health__notice">Einige Datenarten sind nicht freigegeben. Du kannst deine Auswahl unter „Gesundheitsdaten verwalten“ prüfen.</p>
        <div v-if="metrics.length" class="android-health__metrics">
          <div v-for="metric in metrics" :key="metric.key" class="android-health__metric">
            <v-icon size="18" color="primary">{{ metric.icon }}</v-icon>
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }} <small>{{ metric.unit }}</small></strong>
            <span class="android-health__metric-note">{{ metric.note }}</span>
          </div>
        </div>
        <p v-else-if="!health.busy" class="android-health__notice">Noch keine Tageswerte vorhanden. Öffne deine Health-App und prüfe dort die Freigabe für Health Connect.</p>
        <button v-if="health.grantedCategories.includes('workouts')" type="button" class="android-health__workout-toggle" :aria-expanded="showWorkouts ? 'true' : 'false'" @click="showWorkouts = !showWorkouts">
          <v-icon size="19" color="#cbd9d0">mdi-history</v-icon><span>Aufzeichnungen · letzte 7 Tage</span><strong>{{ workouts.length }}</strong>
          <v-icon small color="#cbd9d0">{{ showWorkouts ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </button>
        <div v-if="showWorkouts" class="android-health__workouts">
          <p>Zum Verknüpfen öffne ein abgeschlossenes Roody-Training und wähle „Gesundheitsdaten hinzufügen“.</p>
          <p v-if="!workouts.length">Keine freigegebenen Aufzeichnungen gefunden.</p>
          <div v-for="workout in workouts" :key="workout.workoutId" class="android-health__workout">
            <div><strong>{{ workout.activityName }}</strong><span>{{ workout.sourceName }}</span></div>
            <div><span>{{ dateTime(workout.startedAt) }}</span><strong>{{ duration(workout.durationSeconds) }}</strong></div>
          </div>
          <p v-if="health.snapshot && health.snapshot.truncated">Die Übersicht ist auf 200 Aufzeichnungen begrenzt. Einzelne Tage kannst du direkt bei der Trainingszuordnung durchsuchen.</p>
        </div>
      </template>
    </template>
    <p v-if="health.error" role="alert" class="android-health__error">{{ health.error }}</p>

    <div class="android-health__live">
      <div><strong>Training auf dem Sperrbildschirm</strong><span>Übung, Satz, Dauer und Pausenende</span></div>
      <v-switch :input-value="liveEnabled" :disabled="liveBusy" :loading="liveBusy" color="primary" inset hide-details
        aria-label="Training auf dem Sperrbildschirm anzeigen" @change="setLiveEnabled" />
    </div>
    <div v-if="liveEnabled && live.notificationsEnabled === false" class="android-health__permission">
      <span>Erlaube Benachrichtigungen für die Trainingsanzeige.</span>
      <v-btn text small color="primary" :loading="liveBusy" @click="authorizeLive">Freigeben</v-btn>
    </div>
    <p v-if="liveError" role="alert" class="android-health__error">{{ liveError }}</p>

    <v-dialog v-model="settingsOpen" max-width="520" scrollable dark :persistent="health.busy">
      <v-card class="android-health__settings">
        <v-card-title class="android-health__dialog-title">Deine Freigaben<v-spacer /><v-btn icon :disabled="health.busy" aria-label="Freigaben schliessen" @click="settingsOpen = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text>
          <p>Wir lesen die ausgewählten Daten aus Health Connect. Die Übersicht bleibt auf diesem Handy. Bei einer Trainingszuordnung speichern wir nur die Aufzeichnungs-ID im Konto. Messwerte werden auf diesem Handy aus Health Connect geladen.</p>
          <div class="android-health__types">
            <v-checkbox v-for="type in dataTypes" :key="type.key" v-model="selectedTypes" :value="type.key" :label="type.label"
              color="primary" hide-details :disabled="health.busy" />
          </div>
          <p class="android-health__help">Welche Daten verfügbar sind, hängt von deiner Health-App ab. Puls ist die letzte gespeicherte Messung; eine laufende Pulsmessung wird dadurch nicht gestartet.</p>
          <v-btn block rounded depressed color="primary" :loading="health.busy" :disabled="!selectedTypes.length || !health.supported" @click="connect">{{ health.enabled ? 'Auswahl übernehmen' : 'Freigabe in Health Connect öffnen' }}</v-btn>
          <p v-if="health.error" role="alert" class="android-health__error">{{ health.error }}</p>
          <template v-if="health.enabled">
            <div class="android-health__manage">
              <v-btn text small :disabled="health.busy" @click="service.settings()">Systemfreigaben öffnen</v-btn>
              <v-btn text small color="#ffb5a9" :disabled="health.busy" @click="disconnect">Verbindung trennen</v-btn>
            </div>
            <p class="android-health__help">Beim Trennen wird die lokale Übersicht gelöscht. Bereits bestätigte Zuordnungen kannst du beim jeweiligen Roody-Training lösen.</p>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import healthConnectService from "@/services/healthConnectService"
import trainingLiveService from "@/services/trainingLiveService"
import trainingFeatureAccess from "@/services/trainingFeatureAccess"
import { formatHealthDuration } from "@/services/healthImportMatching"

const DATA_TYPES = [
  { key: "workouts", label: "Trainingsaufzeichnungen" }, { key: "steps", label: "Schritte" },
  { key: "energy", label: "Energie / Kalorien" }, { key: "distance", label: "Distanz" },
  { key: "heartRate", label: "Pulsmessungen" }, { key: "sleep", label: "Schlaf" }, { key: "weight", label: "Gewicht" },
]
export default {
  name: "AndroidHealthCard",
  data: () => ({ service: healthConnectService, health: healthConnectService.state, live: trainingLiveService.viewState,
    settingsOpen: false, showWorkouts: false, selectedTypes: ["workouts", "steps", "energy", "distance"], dataTypes: DATA_TYPES, liveBusy: false, liveError: "" }),
  computed: {
    visible() { return trainingFeatureAccess.isAllowed() && this.health.android },
    workouts() { return this.health.snapshot?.workouts || [] },
    liveEnabled() { return this.$store.state.runWorkoutStore.liveDisplaySettings?.enabled !== false },
    connectionLabel() {
      if (!this.health.enabled) return "Ohne Watch nutzbar"
      return this.health.grantedCategories.length ? "Mit Health Connect verbunden" : "Freigabe in Health Connect fehlt"
    },
    lastSyncLabel() { return this.health.lastSyncAt ? `Letzter Abgleich: ${this.dateTime(this.health.lastSyncAt)}` : "Noch kein erfolgreicher Abgleich" },
    metrics() {
      const snapshot = this.health.snapshot
      if (!snapshot) return []
      const data = snapshot.daily
      const day = snapshot.day === this.$moment().format("YYYY-MM-DD") ? "Heute" : this.$moment(snapshot.day).format("DD. MMM")
      const candidates = [
        ["steps", "Schritte", "mdi-shoe-print", "", day],
        ["activeEnergyKilocalories", "Aktive Energie", "mdi-fire", "kcal", day],
        ["totalEnergyKilocalories", "Gesamtenergie", "mdi-fire", "kcal", day],
        ["distanceMeters", "Distanz", "mdi-map-marker-distance", "km", day],
        ["sleepSeconds", "Schlaf", "mdi-weather-night", "", data.sleepWindowStart ? `Seit ${this.dateTime(data.sleepWindowStart)}` : ""],
        ["heartRate", "Letzte Pulsmessung", "mdi-heart-pulse", "BPM", data.heartRateMeasuredAt ? this.dateTime(data.heartRateMeasuredAt) : ""],
        ["weightKilograms", "Letztes Gewicht", "mdi-scale-bathroom", "kg", data.weightMeasuredAt ? this.dateTime(data.weightMeasuredAt) : ""],
      ]
      return candidates.filter(([key]) => typeof data[key] === "number").map(([key, label, icon, unit, note]) => ({
        key, label, icon, unit, note,
        value: key === "sleepSeconds" ? formatHealthDuration(data[key]) :
          (key === "distanceMeters" ? data[key] / 1000 : data[key]).toLocaleString("de-CH", { maximumFractionDigits: ["distanceMeters", "weightKilograms"].includes(key) ? 1 : 0 }),
      }))
    },
  },
  watch: {
    visible(value) { if (!value) { this.settingsOpen = false; this.showWorkouts = false } },
  },
  methods: {
    dateTime(value) { return this.$moment(value).format("DD. MMM · HH:mm") },
    duration: formatHealthDuration,
    openSettings() { this.selectedTypes = this.health.categories.length ? [...this.health.categories] : ["workouts", "steps", "energy", "distance"]; this.settingsOpen = true },
    async connect() { await this.service.connect(this.selectedTypes); if (this.health.enabled && !this.health.error) this.settingsOpen = false },
    async disconnect() { await this.service.disconnect(); if (!this.health.error) this.settingsOpen = false },
    async setLiveEnabled(enabled) {
      if (!trainingFeatureAccess.isAllowed()) return
      this.$store.commit("runWorkoutStore/SET_LIVE_DISPLAY_SETTINGS", { enabled })
      if (enabled) await this.authorizeLive()
    },
    async authorizeLive() {
      this.liveBusy = true; this.liveError = ""
      try {
        if (!await trainingLiveService.authorize()) this.liveError = "Bitte die Benachrichtigungen in den Android-Einstellungen freigeben. Die Trainingsanzeige benötigt die neue Android-App."
      } catch (_) {
        this.liveError = "Die Freigabe konnte nicht geöffnet werden. Bitte erneut versuchen."
      } finally { this.liveBusy = false }
    },
  },
}
</script>

<style scoped>
.android-health { margin: 12px 0 22px; padding: 22px; color: #edf4ef; background: linear-gradient(140deg, #20352a, #15221c 75%); border: 1px solid #ffffff18; border-radius: 24px; scroll-margin-top: 24px; }
.android-health__header { display: flex; align-items: center; gap: 13px; }
.android-health__icon { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 15px; background: #00963f22; flex-shrink: 0; }
.android-health__heading { flex: 1; min-width: 0; }
.android-health__eyebrow { color: #edf4ef; font-size: 10px; font-weight: 700; letter-spacing: 1.2px; }
.android-health h2 { margin: 3px 0; font-size: 20px; font-weight: 700; line-height: 1.3; }
.android-health p { color: #c5d3c9; font-size: 13px; line-height: 1.6; }
.android-health__heading p { margin: 0; font-size: 12px; }
.android-health__intro { margin: 16px 0; }
.android-health__intro .v-btn { max-width: 100%; font-size: 12px; letter-spacing: .2px; }
.android-health__sync { display: flex; justify-content: space-between; align-items: center; margin: 16px 0 8px; color: #c5d3c9; font-size: 12px; }
.android-health__metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; }
.android-health__metric { display: flex; align-items: flex-start; flex-direction: column; gap: 5px; padding: 15px; border-radius: 16px; background: #ffffff07; }
.android-health__metric > span { color: #c5d3c9; font-size: 12px; }
.android-health__metric strong { font-size: 24px; font-variant-numeric: tabular-nums; }
.android-health__metric strong small { font-size: 12px; font-weight: 400; color: #c5d3c9; }
.android-health__metric .android-health__metric-note { font-size: 11px; }
.android-health__workout-toggle { display: flex; width: 100%; min-height: 48px; gap: 10px; align-items: center; margin-top: 10px; text-align: left; font-size: 12px; }
.android-health__workout-toggle span { flex: 1; }
.android-health__workouts { max-height: 380px; overflow: auto; }
.android-health__workout { display: flex; justify-content: space-between; gap: 12px; padding: 12px 0; border-top: 1px solid #ffffff12; font-size: 12px; }
.android-health__workout > div { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.android-health__workout > div:last-child { text-align: right; flex-shrink: 0; }
.android-health__workout span { color: #c5d3c9; overflow-wrap: anywhere; }
.android-health__live { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 16px; margin-top: 16px; border-top: 1px solid #ffffff18; }
.android-health__live strong, .android-health__live span { display: block; }
.android-health__live strong { font-size: 13px; }
.android-health__live span { font-size: 12px; color: #c5d3c9; margin-top: 4px; }
.android-health__live .v-input { flex: 0 0 auto; margin-top: 0; padding-top: 0; }
.android-health__permission { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; font-size: 12px; }
.android-health .android-health__error, .android-health__settings .android-health__error { padding: 12px; border-radius: 12px; background: #ffb5a912; color: #ffc8bf; font-size: 13px; line-height: 1.6; }
.android-health__notice { margin-top: 14px; }
.android-health__settings { border-radius: 24px !important; background: #17261e !important; }
.android-health__settings p { color: #ccd7d0; line-height: 1.65; font-size: 13px; }
.android-health__dialog-title { font-size: 21px; }
.android-health__types { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 20px 0; }
.android-health__types .v-input { margin: 0; }
.android-health__help { margin-top: 18px; }
.android-health__manage { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
.android-health button:focus-visible { outline: 2px solid var(--v-primary-base, #00963f); outline-offset: 3px; }
@media (max-width: 380px) { .android-health { padding: 16px; } .android-health__types { grid-template-columns: 1fr; } .android-health__metric { padding: 12px; } }
</style>
